import { Component, ViewEncapsulation, input, signal, computed, effect, inject, PendingTasks } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { forkJoin, of, take, catchError } from 'rxjs';
import { CardItemComponent, CardItemData } from '../../molecules/card-item/card-item';
import { OverlineComponent } from '../../atoms/overline/overline';
import { StrapiService } from '../../../core/services/strapi.service';
import { STRAPI_CONFIG } from '../../../core/api/strapi.config';
import {
  buildArchiveItems,
  sortByEffectiveDate,
  sortByRecordedDateDescNullLast,
  sortByStartDateWithNullLast,
  enrichFilterCategories,
} from '../../../pages/archive-page/archive-page.utils';

export interface ArchiveItem {
  title: string;
  excerpt: string;
  href: string;
  tags: Record<string, string>;
  cardData: CardItemData;
}

export interface FilterCategory {
  key: string;
  label: string;
  options: string[];
}

const CONTENT_TYPE_TO_ENDPOINT: Record<string, string> = {
  'asset-type-page': 'asset-type-pages',
  'case-study': 'case-studies',
  'event-page': 'event-pages',
  'industry-page': 'industry-pages',
  'insight-page': 'insight-pages',
  'lead-magnet-page': 'lead-magnet-pages',
  'news-page': 'news-pages',
  'partner': 'partners',
  'webinar-page': 'webinar-pages',  // Phase 45-03 (WBNR-13) — sister of archive-page.component.ts:40 + sitemap.ts:77
};

@Component({
  selector: 'ds-archive-section',
  templateUrl: './archive-section.html',
  styleUrl: './archive-section.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [CardItemComponent, OverlineComponent],
})
export class ArchiveSectionComponent {
  readonly sectionId = input<string>('');
  readonly background = input<string>('');
  readonly overline = input<string>('');
  readonly heading = input<string>('');
  readonly items = input<ArchiveItem[]>([]);
  readonly filterCategories = input<FilterCategory[]>([]);
  readonly pageSize = input<number>(9);
  readonly cssClass = input<string>('');
  readonly contentType = input<string>('');
  readonly footerHtml = input<string>('');
  readonly urlPagination = input(false);
  readonly industryFirst = input(false);
  private readonly doc = inject(DOCUMENT);

  private readonly strapi = inject(StrapiService);
  private readonly config = inject(STRAPI_CONFIG);
  private readonly pendingTasks = inject(PendingTasks);

  readonly selfFetchedItems = signal<ArchiveItem[]>([]);

  readonly effectiveItems = computed(() =>
    this.items().length > 0 ? this.items() : this.selfFetchedItems(),
  );

  readonly enrichedFilterCategories = computed(() =>
    enrichFilterCategories(this.filterCategories(), this.effectiveItems()),
  );

  readonly industryCategory = computed(() => this.industryFirst()
    ? this.enrichedFilterCategories().find(cat => ['industries', 'industry'].includes(cat.key) && cat.options.length > 0)
    : undefined,
  );

  readonly refinementCategories = computed(() =>
    this.enrichedFilterCategories().filter(cat => cat.key !== this.industryCategory()?.key && cat.options.length > 0),
  );

  readonly activeRefinementCount = computed(() =>
    this.refinementCategories().filter(cat => (this.activeFilters()[cat.key]?.size ?? 0) > 0).length,
  );

  readonly activeFilterSummary = computed(() =>
    this.enrichedFilterCategories().flatMap(cat => [...(this.activeFilters()[cat.key] ?? [])]).join(' · '),
  );

  readonly activeFilters = signal<Record<string, Set<string>>>({});
  readonly filterNotice = signal('');
  readonly currentPage = signal(1);

  private readonly orderedIndustryCategories = computed(() => {
    const industry = this.industryCategory();
    return industry ? [industry, ...this.refinementCategories()] : [];
  });

  readonly availableFilterOptions = computed(() => {
    let candidates = this.effectiveItems();
    const options: Record<string, string[]> = {};
    for (const category of this.orderedIndustryCategories()) {
      options[category.key] = category.options.filter(option =>
        candidates.some(item => item.tags[category.key] === option),
      );
      const selected = this.activeFilters()[category.key];
      if (selected?.size) candidates = candidates.filter(item => selected.has(item.tags[category.key]));
    }
    return options;
  });

  readonly filteredItems = computed(() => {
    const all = this.effectiveItems();
    const filters = this.activeFilters();
    return all.filter(item => {
      for (const [category, values] of Object.entries(filters)) {
        if (values.size === 0) continue;
        if (!values.has(item.tags[category] ?? '')) return false;
      }
      return true;
    });
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredItems().length / this.pageSize())),
  );

  readonly paginatedItems = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const size = this.pageSize();
    const start = (page - 1) * size;
    return this.filteredItems().slice(start, start + size);
  });

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  readonly resultCount = computed(() => this.filteredItems().length);
  readonly totalCount = computed(() => this.effectiveItems().length);

  readonly hasActiveFilters = computed(() => {
    const filters = this.activeFilters();
    return Object.values(filters).some(set => set.size > 0);
  });

  constructor() {
    effect(() => {
      if (!this.urlPagination()) return;
      const params = new URLSearchParams(this.doc.location.search);
      const requested = Number(params.get('page') ?? 1);
      const maximumPage = Math.max(1, Math.ceil(this.effectiveItems().length / (this.pageSize() || 9)));
      this.currentPage.set(Number.isSafeInteger(requested) && requested > 0 && requested <= maximumPage ? requested : 1);
      const filters: Record<string, Set<string>> = {};
      for (const category of this.filterCategories()) {
        const values = params.getAll('filter.' + category.key);
        if (values.length) filters[category.key] = new Set(this.industryFirst() ? values.slice(0, 1) : values);
      }
      this.applyIndustryFilters(filters);
    });
    effect(() => {
      const ct = this.contentType();
      if (!ct || this.items().length > 0) return;

      const endpoint = CONTENT_TYPE_TO_ENDPOINT[ct] ?? ct + 's';
      const taskDone = this.pendingTasks.add();

      // Event-page archive embeds sort by startDate asc (mirrors ArchivePageComponent);
      // the client-side sortByStartDateWithNullLast below applies the authoritative
      // nulls-last reorder that Strapi's sort param cannot express.
      //
      // Asset-type-page archive embeds sort by [gridOrder:asc, title:asc] (GORD-02, Spec 35).
      // PG ASC NULLS LAST handles null gridOrder → no client-side reorder (the post-fetch
      // dispatch below also passes rawItems through untouched for asset-type-page).
      //
      // Webinar-page archive embeds sort by recordedDate:desc (WBNR-14, Spec 36 line 99).
      // PITFALL 19 — PG DESC default = NULLS FIRST, so the post-fetch dispatch below
      // applies sortByRecordedDateDescNullLast which is the AUTHORITATIVE reorder.
      //
      // MIRROR: src/app/pages/archive-page/archive-page.component.ts:119 — keep the sort
      // dispatch in lockstep across both archive entry points (Pitfall 20).
      const sortParam: string | string[] =
        ct === 'event-page'        ? 'startDate:asc'
        : ct === 'asset-type-page' ? ['gridOrder:asc', 'title:asc']
        : ct === 'webinar-page'    ? 'recordedDate:desc'
        :                            'publishedAt:desc';

      forkJoin({
        itemsRes: this.strapi.findMany<Record<string, unknown>>(endpoint, 'archive-list', {
          pageSize: 200,
          sort: sortParam,
        }),
        archiveRes: this.strapi.findArchivePageByContentType(ct).pipe(catchError(() => of(null))),
      }).pipe(take(1)).subscribe({
        next: ({ itemsRes, archiveRes }) => {
          const archivePage = archiveRes?.data ?? null;
          const basePath = archivePage?.basePath ?? '';
          const slug = archivePage?.slug ?? '';
          // Asset-type-page archive: API-level order is authoritative — bypass client reorder.
          //
          // Webinar-page archive: PITFALL 19 — sortByRecordedDateDescNullLast IS the
          // authoritative reorder (API-level recordedDate:desc returns nulls at the
          // top per PG DESC default; this util flips them to the bottom).
          //
          // MIRROR: src/app/pages/archive-page/archive-page.component.ts:170 (post-fetch dispatch).
          const sorted =
            ct === 'event-page'        ? sortByStartDateWithNullLast(itemsRes.data)
            : ct === 'asset-type-page' ? itemsRes.data
            : ct === 'webinar-page'    ? sortByRecordedDateDescNullLast(itemsRes.data)
            :                            sortByEffectiveDate(itemsRes.data);
          const filterKeys = this.filterCategories().map((c) => c.key);
          const built = buildArchiveItems(
            sorted, basePath, slug, filterKeys, this.config.baseUrl, ct,
          );
          this.selfFetchedItems.set(built);
          taskDone();
        },
        error: () => taskDone(),
      });
    });
  }

  selectFilter(category: string, value: string): void {
    this.applyIndustryFilters({ ...this.activeFilters(), [category]: new Set(value ? [value] : []) });
    this.currentPage.set(1);
  }

  private applyIndustryFilters(filters: Record<string, Set<string>>): void {
    // Keep the industry -> asset -> issue hierarchy consistent, including old direct links.
    // Wait for items before rejecting restored values while archive data is loading.
    let candidates = this.effectiveItems();
    const cleared: string[] = [];
    const next = { ...filters };
    if (this.industryFirst() && candidates.length > 0) {
      for (const category of this.orderedIndustryCategories()) {
        const selected = next[category.key];
        if (!selected?.size) continue;
        const matches = candidates.filter(item => selected.has(item.tags[category.key]));
        if (matches.length) {
          candidates = matches;
        } else {
          delete next[category.key];
          cleared.push(this.filterLabel(category));
        }
      }
    }
    this.activeFilters.set(next);
    this.filterNotice.set(cleared.length ? `Cleared unavailable filters: ${cleared.join(', ')}.` : '');
  }

  selectedFilter(category: string): string {
    return [...(this.activeFilters()[category] ?? [])][0] ?? '';
  }

  filterLabel(category: FilterCategory): string {
    return category.key === 'faultType' ? 'Issue' : category.label;
  }

  toggleFilter(category: string, value: string): void {
    const current = this.activeFilters();
    const set = new Set(current[category] ?? []);
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
    this.activeFilters.set({ ...current, [category]: set });
    this.currentPage.set(1);
  }

  clearFilters(): void {
    this.activeFilters.set({});
    this.filterNotice.set('');
    this.currentPage.set(1);
  }

  isFilterActive(category: string, value: string): boolean {
    return this.activeFilters()[category]?.has(value) ?? false;
  }

  pageHref(page: number): string {
    const params = new URLSearchParams(this.doc.location.search);
    params.delete('page');
    for (const key of [...params.keys()]) {
      if (key.startsWith('filter.')) params.delete(key);
    }
    for (const [category, values] of Object.entries(this.activeFilters())) {
      for (const value of values) params.append('filter.' + category, value);
    }
    if (page > 1) params.set('page', String(page));
    const query = params.toString();
    return this.doc.location.pathname + (query ? '?' + query : '');
  }

  goToPage(page: number): void {
    const clamped = Math.max(1, Math.min(page, this.totalPages()));
    this.currentPage.set(clamped);
  }
}
